import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Tag,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { convert, LocalDateTime } from "@js-joda/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { BodyOf, ResponseOf } from "../../core/api";
import ErrorBox from "../../shared/components/ErrorBox";
import useApi from "../../shared/hooks/useApi";
import { PlanOfCareDutyId, VisitInstanceId } from "../../shared/schema/schema";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import UndoReassignMissedDutyButton from "./UndoReassignMissedDutyButton";
import { MissedDuty, VisitInstanceDutyMakeUp } from "./reassignMissedDuty.queries";

/**
 * Every target in this dialog falls inside ONE week, so the weekday is the thing
 * a coordinator actually chooses on and the year is noise. The shared
 * `dateFormatter` renders neither — it is built for absolute dates elsewhere in
 * the app — so the dialog formats its own rows.
 *
 * `Intl.DateTimeFormat`, not `DateTimeFormatter.ofPattern`: js-joda cannot render
 * a weekday name or an AM/PM marker without `@js-joda/locale`, and a module-level
 * `ofPattern` carrying either throws at IMPORT time, which took the whole page
 * down the first time this dialog shipped.
 */
const targetDayFormat = Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "numeric",
  day: "numeric",
});
const targetTimeFormat = Intl.DateTimeFormat("en-US", { timeStyle: "short" });

function formatTargetVisit(startTime: LocalDateTime, endTime: LocalDateTime) {
  const start = convert(startTime).toDate();
  const end = convert(endTime).toDate();

  return `${targetDayFormat.format(start)} · ${targetTimeFormat.format(
    start
  )} – ${targetTimeFormat.format(end)}`;
}

function formatOccurrences(occurrences: number) {
  return occurrences === 1 ? "1 occurrence" : `${occurrences} occurrences`;
}

/**
 * `occurrences` is on the server's `ReassignMissedDutyRequest` but reached it
 * after the last `pnpm openapi:local`, so the generated body type does not carry
 * it yet. Drop the intersection when the schema is regenerated.
 */
type ReassignBody = BodyOf<
  "post",
  "./visit_instances/:visitInstanceId/missed_duties/:planOfCareDutyId/reassign"
> & { occurrences?: number };
type ReassignResponse = ResponseOf<
  "post",
  "./visit_instances/:visitInstanceId/missed_duties/:planOfCareDutyId/reassign"
>;

/**
 * How to name a duty in the make-up list. `existingMakeUps` carries ids, counts
 * and the target visit but no code or label, and a duty whose every occurrence is
 * made up has nothing left to miss — so it is absent from `missedDuties` too and
 * its name cannot be recovered from the response at all.
 *
 * The visit modal has it: a duty handed away stays on the visit's own duty list.
 * So the modal passes it in.
 */
export type DutyName = {
  planOfCareDutyId: PlanOfCareDutyId;
  code: string;
  title: string;
};

type Props = {
  disclosure: ReturnType<typeof useDisclosure>;
  dutyNames?: DutyName[];
  visitInstanceId: VisitInstanceId;
};

export default function ReassignMissedDutyDialog(props: Props) {
  const { api, queries } = useApi();
  const toast = useToast();
  const [selectedTargets, setSelectedTargets] = React.useState<
    Partial<Record<PlanOfCareDutyId, VisitInstanceId>>
  >({});
  const [selectedOccurrences, setSelectedOccurrences] = React.useState<
    Partial<Record<PlanOfCareDutyId, number>>
  >({});

  const query = useQuery({
    ...queries.reassignMissedDuty.missedDuties(props.visitInstanceId),
    enabled: props.disclosure.isOpen,
  });

  const dutyNameByDutyId = React.useMemo(() => {
    const names: Partial<Record<PlanOfCareDutyId, DutyName>> = {};

    for (const dutyName of props.dutyNames ?? []) {
      names[dutyName.planOfCareDutyId] = dutyName;
    }

    return names;
  }, [props.dutyNames]);

  const mutation = useMutation({
    mutationFn: (params: { planOfCareDutyId: PlanOfCareDutyId; body: ReassignBody }) =>
      api.post("./visit_instances/:visitInstanceId/missed_duties/:planOfCareDutyId/reassign", {
        path: {
          planOfCareDutyId: params.planOfCareDutyId,
          visitInstanceId: props.visitInstanceId,
        },
        body: params.body,
      }),
    onSuccess: (
      data: ReassignResponse,
      variables: { planOfCareDutyId: PlanOfCareDutyId; body: ReassignBody }
    ) => {
      switch (data.result) {
        case "SUCCESS":
          toast({ title: "Duty reassigned", status: "success", position: "top-right" });
          break;
        case "NOT_MISSED":
        case "TARGET_NOT_ELIGIBLE":
          toast({
            title: "This visit's duties changed while the list was open. Refreshing.",
            status: "warning",
            position: "top-right",
          });
          // The picked target may no longer be in the refreshed list, which would leave the
          // RadioGroup showing nothing selected while selectedVisitInstanceId stays non-null -
          // keeping the Reassign button enabled and able to resubmit the same dead id.
          setSelectedTargets((prev) => {
            const next = { ...prev };
            delete next[variables.planOfCareDutyId];
            return next;
          });
          break;
      }

      query.refetch();
    },
    onError: (error) => {
      toast({
        title: "Error reassigning duty",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });

      // The one way to ask for more occurrences than are missed is to ask against
      // a stale list, and the server answers that with a 400. Refetching is what
      // turns the message into a list the coordinator can act on again, and it
      // costs nothing on the errors that are not about staleness.
      query.refetch();
    },
  });

  const handleReassign = (duty: MissedDuty) => {
    const targetVisitInstanceId = selectedTargets[duty.planOfCareDutyId];
    if (targetVisitInstanceId === undefined) {
      return;
    }

    // Clamped rather than trusted: the picked count was chosen against a list
    // that may since have refetched with fewer occurrences outstanding, and more
    // than are missed is a 400 rather than a smaller make-up.
    const occurrences = Math.min(
      selectedOccurrences[duty.planOfCareDutyId] ?? 1,
      duty.missedOccurrences
    );

    mutation.mutate({
      planOfCareDutyId: duty.planOfCareDutyId,
      // Absent means one, which is what the server wrote before a duty could be
      // missed more than once on a visit.
      body: occurrences > 1 ? { targetVisitInstanceId, occurrences } : { targetVisitInstanceId },
    });
  };

  return (
    <Modal {...props.disclosure} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Make up duties on another visit</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {(() => {
            switch (query.status) {
              case "pending":
                return (
                  <Stack spacing={4}>
                    <Skeleton height="60px" />
                    <Skeleton height="60px" />
                  </Stack>
                );
              case "error":
                return <ErrorBox error={query.error} resetErrorBoundary={query.refetch} />;
              case "success":
                return (
                  <Stack spacing={8}>
                    {query.data.missedDuties.length === 0 ? (
                      <Text color="gray.600">This visit has no missed duties.</Text>
                    ) : (
                      <Stack divider={<Divider />} spacing={6}>
                        {query.data.missedDuties.map((duty) => (
                          <MissedDutySection
                            key={duty.planOfCareDutyId}
                            duty={duty}
                            isReassigning={
                              mutation.isPending &&
                              mutation.variables?.planOfCareDutyId === duty.planOfCareDutyId
                            }
                            occurrences={Math.min(
                              selectedOccurrences[duty.planOfCareDutyId] ?? 1,
                              duty.missedOccurrences
                            )}
                            selectedVisitInstanceId={selectedTargets[duty.planOfCareDutyId] ?? null}
                            onOccurrencesChange={(occurrences) =>
                              setSelectedOccurrences((prev) => ({
                                ...prev,
                                [duty.planOfCareDutyId]: occurrences,
                              }))
                            }
                            onReassign={() => handleReassign(duty)}
                            onSelect={(visitInstanceId) =>
                              setSelectedTargets((prev) => ({
                                ...prev,
                                [duty.planOfCareDutyId]: visitInstanceId,
                              }))
                            }
                          />
                        ))}
                      </Stack>
                    )}

                    {query.data.existingMakeUps.length > 0 && (
                      <Box>
                        <Heading as="h3" mb={3} size="sm">
                          Already made up
                        </Heading>
                        <Stack divider={<Divider />} spacing={3}>
                          {query.data.existingMakeUps.map((makeUp) => (
                            <ExistingMakeUpRow
                              key={`${makeUp.planOfCareDutyId}-${makeUp.targetVisitInstanceId}`}
                              dutyName={dutyNameByDutyId[makeUp.planOfCareDutyId]}
                              makeUp={makeUp}
                              visitInstanceId={props.visitInstanceId}
                              onUndone={() => query.refetch()}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                );
            }
          })()}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={props.disclosure.onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function MissedDutySection(props: {
  duty: MissedDuty;
  isReassigning: boolean;
  occurrences: number;
  selectedVisitInstanceId: VisitInstanceId | null;
  onOccurrencesChange: (occurrences: number) => void;
  onReassign: () => void;
  onSelect: (visitInstanceId: VisitInstanceId) => void;
}) {
  const { duty } = props;
  const hasEligibleTargets = duty.eligibleTargetVisits.length > 0;

  return (
    <Box>
      <HStack align="baseline" spacing={2}>
        <Text fontWeight="bold">
          {duty.code} — {duty.label}
        </Text>
        {/* Only for a duty missed more than once: one missed occurrence is the
            ordinary case and a badge on every row would say nothing. */}
        {duty.missedOccurrences > 1 && (
          <Tag colorScheme="orange" size="sm">
            {formatOccurrences(duty.missedOccurrences)} missed
          </Tag>
        )}
      </HStack>
      <Text color="gray.600" fontSize="sm" mb={2}>
        {duty.sectionName}
      </Text>

      {hasEligibleTargets ? (
        <RadioGroup
          value={props.selectedVisitInstanceId?.toString() ?? ""}
          onChange={(value) => props.onSelect(VisitInstanceId.parse(Number(value)))}
        >
          <Stack spacing={2}>
            {duty.eligibleTargetVisits.map((target) => (
              <Radio key={target.visitInstanceId} value={target.visitInstanceId.toString()}>
                {formatTargetVisit(target.startTime, target.endTime)}
                {target.caregiverName !== null ? ` · ${target.caregiverName}` : ""}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      ) : (
        <Text color="gray.500" fontSize="sm">
          No visit in this week can take this duty — the week&apos;s other visits have already
          clocked out, already ended, or already carry it.
        </Text>
      )}

      <HStack mt={3} spacing={3}>
        <Button
          isDisabled={!hasEligibleTargets || props.selectedVisitInstanceId === null}
          isLoading={props.isReassigning}
          size="sm"
          onClick={props.onReassign}
        >
          Reassign
        </Button>
        {/* A count to choose only where there is a choice. A duty missed once can
            only move once, and offering a one-item control would just be noise. */}
        {duty.missedOccurrences > 1 && (
          <HStack spacing={2}>
            <Text color="gray.600" fontSize="sm" whiteSpace="nowrap">
              Occurrences to make up
            </Text>
            <Select
              isDisabled={!hasEligibleTargets}
              size="sm"
              value={props.occurrences}
              width="16"
              onChange={(event) => props.onOccurrencesChange(Number(event.target.value))}
            >
              {Array.from({ length: duty.missedOccurrences }, (_unused, index) => index + 1).map(
                (occurrence) => (
                  <option key={occurrence} value={occurrence}>
                    {occurrence}
                  </option>
                )
              )}
            </Select>
          </HStack>
        )}
      </HStack>
    </Box>
  );
}

function ExistingMakeUpRow(props: {
  dutyName: DutyName | undefined;
  makeUp: VisitInstanceDutyMakeUp;
  visitInstanceId: VisitInstanceId;
  onUndone: () => void;
}) {
  const { makeUp } = props;

  return (
    <HStack align="start" justify="space-between" spacing={4}>
      <Box>
        <Text fontWeight="bold">
          {props.dutyName !== undefined
            ? `${props.dutyName.code} — ${props.dutyName.title}`
            : "Duty no longer on this visit"}
        </Text>
        <Text color="gray.600" fontSize="sm">
          {formatOccurrences(makeUp.occurrences)} on{" "}
          {formatTargetVisit(makeUp.targetStartTime, makeUp.targetEndTime)}
          {makeUp.targetCaregiverName !== null ? ` · ${makeUp.targetCaregiverName}` : ""}
        </Text>
      </Box>
      {/* The target goes with the undo so it retracts THIS make-up and not every
          make-up of the duty. */}
      <UndoReassignMissedDutyButton
        planOfCareDutyId={makeUp.planOfCareDutyId}
        targetVisitInstanceId={makeUp.targetVisitInstanceId}
        visitInstanceId={props.visitInstanceId}
        onUndone={props.onUndone}
      />
    </HStack>
  );
}
